import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import MobileDrawerMenu from '#component/general/mobile-drawer-menu';
import DeleteDialog from '#component/project/delete-dialog';
import { deleteProject } from '#action/project/dashboard';
import HighlightWord from '#component/general/highlight-word/highlight-word';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';
import UsersButton from './users-button/users-button';
import DevicesButton from './devices-button/devices-button';
import propTypes from './prop-types';
import {
  StyledCard,
  CardHeaderContainer,
  HeaderTextContainer,
  StyledCardHeader,
  StyledListItemIcon,
  StyledCardContent,
  Title2,
  UsersButtonContainer
} from './styles';
import { getProjectDateRange, isProjectExpired } from '#helper';

function CardView(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { project, account, reloadProjects } = props;
  const { project: { users, devices } } = props;
  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(null);
  const formattedProjectName = project.title.charAt(0).toUpperCase() + project.title.slice(1);
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const highlightedSearchResult = getHighlightedSearchResult(listFilter);

  const handleOpen = event => {
    setIsDrawerMenuOpen(event.currentTarget);
  };

  const handleClose = () => {
    setIsDrawerMenuOpen(null);
  };

  const handleCloseDialog = () => {
    setIsDeleteDialogOpen(false);
    setIsDrawerMenuOpen(false);
  };

  const handleEditProjectClick = () => history.push(`/project/edit/${project.id}`);
  const handleAudutTrailClick = () => history.push(`/project-log?project=${project.id}`);
  const handleDeleteClick = () => setIsDeleteDialogOpen(true);

  const handleDelete = async () => {
    setIsDrawerMenuOpen(false);

    try {
      const { success, error } = await dispatch(deleteProject(project.id));
      if (success) {
        setIsDeleteDialogOpen(false);
        reloadProjects();
      } else {
        console.error('Failed to delete project:', error);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
    }
  };

  const menuItems = [
    { name: 'Edit project', onClick: handleEditProjectClick, icon: <ModeEditIcon /> },
    { name: 'Audit trail', onClick: handleAudutTrailClick, icon: <SpeakerNotesIcon /> },
    { name: 'Delete', onClick: handleDeleteClick, icon: <DeleteIcon /> }
  ];

  return (
    <StyledCard>
      <CardHeaderContainer>
        <HeaderTextContainer>
          <StyledCardHeader
            onClick={() => history.push(`/project/edit/${project.id}`)}
            tabIndex={0}
            aria-label={`Edit project ${project.name}`}
            subheader={
              <Typography style={{ marginRight: '60px', whiteSpace: 'initial', overflowWrap: 'break-word' }} fontWeight={400}>
                {highlightedSearchResult ? <HighlightWord
                  textToHighlight={formattedProjectName}
                  searchWord={highlightedSearchResult}
                /> : formattedProjectName}
                {isProjectExpired(project) && ' (expired)'}
              </Typography>
            }
          />
        </HeaderTextContainer>
        <StyledListItemIcon
          onClick={e => {
            e.stopPropagation();
            handleOpen(e);
          }}
        >
          <MoreHorizIcon />
        </StyledListItemIcon>
      </CardHeaderContainer>
      {users ? (
        <StyledCardContent>
          <Title2
            color="textSecondary"
            gutterBottom
          >
            Users
          </Title2>
          <UsersButtonContainer>
            <UsersButton project={project} />
          </UsersButtonContainer>
        </StyledCardContent>
      ) : (
        ''
      )}
      {devices ? (
        <StyledCardContent>
          <Title2
            color="textSecondary"
            gutterBottom
          >
            Devices
          </Title2>
          <DevicesButton devicesCount={devices} project={project} />
        </StyledCardContent>
      ) : (
        ''
      )}
      <StyledCardContent>
        <Title2
          color="textSecondary"
          gutterBottom
        >
          Date
        </Title2>
        <Typography color="textSecondary" fontWeight={500}>
          {getProjectDateRange(project, account && account.measurementSystem)}
        </Typography>
      </StyledCardContent>
      <MobileDrawerMenu
        isOpen={isDrawerMenuOpen}
        onClose={handleClose}
        menuTitle={formattedProjectName}
        menuItems={menuItems}
      />
      <DeleteDialog
        title={project.title}
        open={isDeleteDialogOpen}
        onClose={handleCloseDialog}
        onDelete={handleDelete}
      />
    </StyledCard>
  );
}

CardView.propTypes = propTypes;

export default CardView;
